import { IrdApiCapabilities } from '../field-decorators';
import { FilterOption, IrdPropertyType } from '../property';
import { IrdValidator } from '../validator';

/**
 * Turns a raw value into what the user sees — declared, never written as a function per page.
 * `IrdValueFormatter` reads it; a property that declares none gets its type's entry in
 * `IRD_PROPERTY_TYPE_DEFAULTS`.
 *
 * - `text` shows strings, numbers and booleans as they are; an object has no text form.
 * - `number` groups digits in the active locale. `digitsInfo` is Angular's `1.0-2` notation.
 * - `date` uses a named or pattern format (`shortDate` unless declared) in the active locale.
 * - `list` joins a collection with the declared separator, each item formatted by the type default.
 * - `custom` renders the final string itself.
 * - `translation-key` renders a translation key, which is resolved in the active language.
 *   It is how a declaration stays framework-free and still produces a translated value — a
 *   boolean reading "Yes" rather than `true`, say.
 *
 * Plain data and plain functions only: this library holds no Angular, so a pipe cannot be declared.
 */
export type IrdFormat<O = unknown> =
  | { kind: 'text' }
  | { kind: 'number'; digitsInfo?: string }
  | { kind: 'date'; dateFormat?: string }
  | { kind: 'list'; separator: string }
  | { kind: 'custom'; render: (value: O) => string }
  | { kind: 'translation-key'; render: (value: O) => string };

/**
 * Where an icon comes from. The application has three sources, and a table may show any of them:
 *
 * - `font` is a ligature of an icon font — `'warning'` in Material Icons unless `fontSet` names another.
 * - `svg` is an SVG registered with the icon registry, e.g. the `seepex` set from the shared icon library.
 * - `url` is an image file, e.g. one the backend serves. A path starting with `/` stays inside the application.
 */
export type IrdIconRef = { font: string; fontSet?: string } | { svg: string; namespace?: string } | { url: string };

/**
 * How a value renders: the markup around the string `IrdFormat` produces. The split is "can a pipe
 * alone produce this" — a string is a format, markup is a render mode — so tooltip, clipboard and
 * export take the format and ignore the render mode.
 *
 * - `text` is the formatted string and nothing else.
 * - `color` shows a swatch of the value beside its text.
 * - `icon` shows the icon `icon(value)` names, or the image at the value itself when no `icon` is
 *   declared. `null` means this value has no icon and reads as text.
 * - `link` makes the formatted string a link to `url(value)`. A URL starting with `/` stays inside
 *   the application; `null` means this value has no target and reads as text.
 * - `json` shows the value as indented JSON.
 */
export type IrdRenderMode<O = unknown> =
  | { kind: 'text' }
  | { kind: 'color' }
  | { kind: 'icon'; icon?: (value: O) => IrdIconRef | null }
  | { kind: 'link'; url: (value: O) => string | null }
  | { kind: 'json' };

/**
 * The presentation attributes a layer may override. Every field is optional because a layer
 * declares only what it changes; everything else is inherited from its parent.
 *
 * Written through `property(key, (p) => …)`, never by hand. Its counterparts: what the field is
 * lives in {@link IrdSchemaFact}, what the API permits in {@link IrdApiCapabilities}, and what a
 * component reads in {@link IrdResolvedProperty}. Which slot takes what: `docs/metadata/cookbook.md` §1.
 */
export interface IrdPropertyPresentation<O = unknown> {
  readonly translationKey?: string;
  readonly format?: IrdFormat<O>;
  readonly renderAs?: IrdRenderMode<O>;
  readonly emptyMarker?: string;
  /**
   * Whether this view intends to offer sorting on the property. Intent only: it is bounded by
   * {@link IrdApiCapabilities.sortable}, and the fold resolves the two into
   * {@link IrdResolvedProperty.sortBy}.
   */
  readonly sortable?: boolean;
  /**
   * The path a sort request sends when it is neither the field's own name nor its backend name —
   * a nested `maintenanceRule.id`, say. Absent, the sort uses the backend name.
   */
  readonly sortField?: string;
  /** Cuts overlong text with an ellipsis and shows the whole value as a tooltip. */
  readonly truncate?: boolean;
  /** A CSS width the column may not exceed, e.g. `'200px'`. */
  readonly maxWidth?: string;
  /**
   * Whether this view lets the property be changed, overriding the schema's
   * {@link IrdSchemaFact.editable} in either direction — a table keeps an editable field out of
   * inline editing, a form opens up a field the schema declares read-only. Absent, the fact holds.
   * Intent only: a capability of `false` ({@link IrdApiCapabilities.editable}) wins over it.
   */
  readonly editable?: boolean;
}

/**
 * The schema half of a property — facts about the data, not about how it is shown. Declared once,
 * in the entity's declaration; no view changes it. How it is shown is {@link IrdPropertyPresentation}.
 */
export interface IrdSchemaFact {
  readonly key: string;
  readonly type: IrdPropertyType;
  readonly array: boolean;
  /** The entity's own translation key for this property, from the prefix convention. */
  readonly translationKey: string;
  /** The name the backend knows the field by, when it differs from {@link key}. */
  readonly daoName?: string;
  readonly validators: readonly IrdValidator[];
  /** The values the field may take, with their labels. Empty when the field is unconstrained. */
  readonly allowedValues: readonly FilterOption[];
  /**
   * Whether the field may be changed, unless a view says otherwise
   * ({@link IrdPropertyPresentation.editable}). Whether a table edits it in place is the table's call.
   */
  readonly editable: boolean;
  /**
   * What the API permits, as opposed to what a view intends (ADR-0003). A fact about the API,
   * taken over unchanged from the entity level: no view can declare, widen or drop it.
   * `undefined` means unknown, not forbidden.
   */
  readonly capabilities?: IrdApiCapabilities;
}

/**
 * One property as a component reads it: the schema fact and the resolved presentation, paired.
 *
 * Instances are created once, by the fold, and never change afterwards — which is what lets a
 * pure pipe memoise on argument identity without a cache of its own.
 *
 * Read, never declared: {@link IrdSchemaFact} and {@link IrdPropertyPresentation} are where it comes from.
 */
export interface IrdResolvedProperty<T = unknown, O = unknown> {
  readonly key: string & keyof T;
  readonly type: IrdPropertyType;
  readonly array: boolean;
  readonly translationKey: string;
  readonly format?: IrdFormat<O>;
  /** Always set: the declared mode, else the type's (`IRD_RENDER_MODE_DEFAULTS`). */
  readonly renderAs: IrdRenderMode<O>;
  readonly emptyMarker: string;
  readonly capabilities?: IrdApiCapabilities;
  readonly daoName?: string;
  readonly validators: readonly IrdValidator[];
  readonly allowedValues: readonly FilterOption[];
  /**
   * The field name a sort request sends, or `undefined` when this view offers no sort here.
   *
   * Set only where the view intends to sort **and** the API does not refuse it: a capability of
   * `false` removes the sort, an unset one leaves it alone — unknown is not forbidden (ADR-0003).
   */
  readonly sortBy?: string;
  readonly truncate: boolean;
  readonly maxWidth: string;
  readonly editable: boolean;
}

/**
 * A run of properties shown together. A group with no declared key is anonymous: it exists to
 * hold properties that were never assigned to a named group, at the position they were ordered in.
 */
export interface IrdResolvedGroup<T = unknown> {
  readonly key: string;
  readonly anonymous: boolean;
  /** Absent on anonymous groups — there is no heading to translate. */
  readonly translationKey?: string;
  readonly collapsible: boolean;
  /** The initial value on every visit. Collapse state is not persisted and not in the URL. */
  readonly collapsed: boolean;
  readonly properties: readonly IrdResolvedProperty<T>[];
}

/**
 * Where each resolved fact came from. Both a debugging aid and a test oracle: a resolution test
 * can assert provenance and not only the value.
 */
export interface IrdAttributeProvenance {
  readonly key: string;
  /**
   * The layer that removed this property, if one did, or {@link IRD_PRIMARY_KEY_DEFAULT} for a
   * primary key no view asked for. Excluded properties stay findable here.
   */
  readonly excludedAt?: string;
  /** Attribute name to the name of the layer that contributed the winning value. */
  readonly attributes: Readonly<Record<string, string>>;
}

/** `excludedAt` of a primary key that no layer removed: the default hid it, not a declaration. */
export const IRD_PRIMARY_KEY_DEFAULT = 'primary-key-default';

/** The marker shown where a property has no value, unless a layer overrides it. */
export const IRD_DEFAULT_EMPTY_MARKER = '—';

/**
 * What a chain folds into, and the only thing a component takes: the value-object half of
 * ADR-0001's builder/value-object split. It has nothing to declare and no path back to the layers
 * it was built from.
 *
 * `context` carries the context as a literal type, so a detail model and a table model are not
 * assignable to each other even though their shapes are identical.
 */
export class IrdResolvedMetadata<T, C extends string = string> {
  constructor(
    public readonly context: C,
    /**
     * The property that identifies a record, from the entity level. A schema fact, so no view
     * changes it; whether it is shown is a separate decision (`showPrimaryKey()`).
     */
    public readonly primaryKey: string & keyof T,
    /** Groups, already ordered and already filtered, each holding the pairs a component renders. */
    public readonly groups: readonly IrdResolvedGroup<T>[],
    /**
     * Translation key of the entity's display name ("IoT map"), for texts about the record as a
     * whole rather than one property. The most derived layer that declares one wins.
     */
    public readonly entityNameKey: string | undefined,
    private readonly provenance: ReadonlyMap<string, IrdAttributeProvenance>,
  ) {}

  /**
   * Which layer contributed each resolved fact about a property, and — for a property that is not
   * in {@link groups} — which layer removed it. Excluded properties stay findable here.
   */
  public explain(key: string): IrdAttributeProvenance | undefined {
    return this.provenance.get(key);
  }
}

/**
 * What a table context builds: the resolved model plus what the table decides as a whole. Whether
 * a cell is edited in place is the table's switch **and** the property's resolved `editable`.
 */
export class IrdResolvedTableMetadata<T> extends IrdResolvedMetadata<T, 'table'> {
  constructor(
    primaryKey: string & keyof T,
    groups: readonly IrdResolvedGroup<T>[],
    entityNameKey: string | undefined,
    provenance: ReadonlyMap<string, IrdAttributeProvenance>,
    /** The table edits its editable cells in place. */
    public readonly inlineEditing: boolean,
  ) {
    super('table', primaryKey, groups, entityNameKey, provenance);
  }
}
